/**
 * What the home screen decides before it renders anything.
 *
 * The screen is context-aware — a scorer with a match in his hand sees something different
 * from someone who opened the app for the first time — and "which of two screens is this"
 * is exactly the kind of rule that becomes six nested ternaries in JSX and stops being
 * checkable. So the decisions live here, as functions, with a test.
 *
 * Nothing in this file fetches. The screen owns the requests; this owns what their answers
 * mean.
 */
import { formatRate } from "@sportsos/engine";
import type { PlayerStatsResponse } from "@sportsos/engine";
import type { Store } from "../localStore.ts";

/**
 * Which home screen this is.
 *
 * `new` is not "has no account" — it is "has nothing to come back to". A scorer whose first
 * match is half-scored has something at stake and gets the returning layout, because the
 * hero pitch is worthless next to an innings he might lose.
 */
export type HomeMode = "new" | "returning";

export interface HomeContext {
  /** Matches the server has for this account. Null while the request is in flight. */
  readonly matchCount: number | null;
  /** A part-scored match on this device. */
  readonly hasActiveMatch: boolean;
}

export function homeMode({ matchCount, hasActiveMatch }: HomeContext): HomeMode {
  if (hasActiveMatch) return "returning";
  // Null — still loading — reads as `new` rather than flashing a returning layout with no
  // rows in it. The empty version of this screen is a pitch, and a pitch that appears for
  // 300ms under a scorer's finger is worse than one that stays.
  return matchCount === null || matchCount === 0 ? "new" : "returning";
}

// ── The value props (first visit only) ──────────────────────────────────────

const VALUE_PROPS_KEY = "sportsos.valueProps.dismissed";

/**
 * The three statements under the hero, and why they are these three.
 *
 * Each names one of the failures the product is a reaction to (PRD §1.2): a scorer losing an
 * over to a dead phone, an incumbent that cannot correct a mis-tapped ball, and an app that
 * charges for the scorecard of a Sunday game. They are compact because they are read once —
 * the whole section is dismissible and never returns.
 */
export const VALUE_PROPS: readonly string[] = [
  "Never lose a ball",
  "Fix any mistake",
  "no ads",
];

export function valuePropsDismissed(store: Store): boolean {
  try {
    return store.getItem(VALUE_PROPS_KEY) === "1";
  } catch {
    // A browser refusing storage shows them again next visit. Harmless; the alternative is
    // a home screen that fails to render because of a preference.
    return false;
  }
}

export function dismissValueProps(store: Store): void {
  try {
    store.setItem(VALUE_PROPS_KEY, "1");
  } catch {
    // Same trade as above.
  }
}

// ── The quick stats ribbon ──────────────────────────────────────────────────

/**
 * Which player this account is, when it has claimed one (#42's flow, P1 ID-4).
 *
 * Read from the device rather than from `/api/me`, because `AuthUser` carries no player id —
 * a user and a player are separate rows precisely so a scorer can enter people who will never
 * have an account. Until the claim ships, this is simply absent and the ribbon shows dashes.
 */
const CLAIMED_PLAYER_KEY = "sportsos.claimedPlayer";

export function claimedPlayerId(store: Store): string | null {
  try {
    const id = store.getItem(CLAIMED_PLAYER_KEY);
    return id === null || id === "" ? null : id;
  } catch {
    return null;
  }
}

export function rememberClaimedPlayer(store: Store, playerId: string): void {
  try {
    store.setItem(CLAIMED_PLAYER_KEY, playerId);
  } catch {
    // The ribbon falls back to dashes. Nothing else depends on it.
  }
}

/**
 * `12 matches · 342 runs · Best: 87* · Avg: 28.50`, or the same shape in dashes.
 *
 * Dashes rather than zeroes, and the shape kept either way. `0 runs · Avg: 0.00` is a claim
 * about a scorer who has simply never been in a scorecard, and a ribbon that collapses to
 * nothing makes the row above it jump the first time stats arrive.
 *
 * The average goes through the engine's `formatRate` rather than a local `toFixed`, so a
 * batting average is spelled the same here as an economy rate is on the scorecard — one
 * spelling of a number across every surface is the whole point of `format.ts`. `NO_DATA` is
 * that module's dash for the same reason: two characters for "nothing to show" in one line
 * would read as two different states.
 */
const NO_DATA = formatRate(null);

export function quickStats(stats: PlayerStatsResponse | null): string {
  if (stats === null) {
    return `${NO_DATA} matches · ${NO_DATA} runs · Best: ${NO_DATA} · Avg: ${NO_DATA}`;
  }
  const { batting } = stats;
  const best =
    batting.innings === 0
      ? NO_DATA
      : `${batting.highScore}${batting.highScoreNotOut ? "*" : ""}`;
  return [
    `${batting.matches} ${batting.matches === 1 ? "match" : "matches"}`,
    `${batting.runs} runs`,
    `Best: ${best}`,
    `Avg: ${formatRate(batting.average)}`,
  ].join(" · ");
}
